import React, { useEffect, useMemo, useState } from 'react'
import { Search, Package } from 'lucide-react'
import { useInventory } from '../../contexts/InventoryContext'
import { useProductAttributes } from '../../hooks/use-product-attributes'
import {
	buildProductNameGroups,
	filterProductGroups,
	type ProductNameGroup,
} from '../../utils/productGrouping'
import { ProductGroupCard } from '../inhouse/product-group-card'
import {
	ProductVariantMatrixModal,
	type VariantAddSelection,
} from '../inhouse/product-variant-matrix-modal'

interface StockCountItemPickerProps {
	adminId: string | null
	addedQtyByProductId: Record<string, number>
	onAdd: (selections: VariantAddSelection[]) => void
}

/**
 * In-House Shipment style picker for stock counting: grouped cards, then a
 * color × size matrix. Stock limits are ignored because a physical count may
 * exceed what the system thinks is on hand.
 */
export function StockCountItemPicker({
	adminId,
	addedQtyByProductId,
	onAdd,
}: StockCountItemPickerProps) {
	const { allItems, allItemsLoading, fetchAllItems } = useInventory()
	const { colors, sizes } = useProductAttributes(adminId)
	const [searchQuery, setSearchQuery] = useState('')
	const [selectedGroup, setSelectedGroup] = useState<ProductNameGroup | null>(null)

	useEffect(() => {
		if (adminId) void fetchAllItems()
	}, [adminId, fetchAllItems])

	const productGroups = useMemo(
		() =>
			buildProductNameGroups(allItems, colors, sizes, (productId) => {
				const item = allItems.find((i) => i.id === productId)
				return item?.quantity ?? 0
			}),
		[allItems, colors, sizes]
	)

	const filteredGroups = useMemo(
		() => filterProductGroups(productGroups, searchQuery, colors),
		[productGroups, searchQuery, colors]
	)

	function handleAddVariants(selections: VariantAddSelection[]) {
		onAdd(selections)
		setSelectedGroup(null)
	}

	return (
		<div className="flex flex-col gap-3 h-full min-h-0">
			<div className="relative">
				<Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
				<input
					type="search"
					value={searchQuery}
					onChange={(e) => setSearchQuery(e.target.value)}
					placeholder="Search name, SKU, reference..."
					className="w-full pl-10 pr-3 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-teal-500"
				/>
			</div>

			{allItemsLoading && allItems.length === 0 ? (
				<div className="flex-1 flex items-center justify-center text-sm text-gray-500">
					Loading products...
				</div>
			) : filteredGroups.length === 0 ? (
				<div className="flex-1 flex flex-col items-center justify-center gap-2 text-sm text-gray-500 py-10">
					<Package className="h-8 w-8 text-gray-300" />
					<span>No products match your search</span>
				</div>
			) : (
				<div className="flex-1 overflow-y-auto space-y-2 pr-1">
					{filteredGroups.slice(0, 80).map((group) => {
						const already =
							group.variants?.reduce(
								(sum, v) => sum + (addedQtyByProductId[v.id] || 0),
								0
							) ?? 0
						return (
							<div key={group.productName} className="relative">
								<ProductGroupCard
									group={group}
									density="list"
									showPricing={false}
									onSelect={() => setSelectedGroup(group)}
								/>
								{already > 0 && (
									<span className="absolute top-2 right-2 text-[11px] font-semibold bg-teal-100 text-teal-800 dark:bg-teal-900/40 dark:text-teal-200 px-2 py-0.5 rounded-full">
										{already} counted
									</span>
								)}
							</div>
						)
					})}
				</div>
			)}

			{selectedGroup && (
				<ProductVariantMatrixModal
					group={selectedGroup}
					colors={colors}
					sizes={sizes}
					showPricing={false}
					showOutOfStock
					ignoreStock
					hideStockInfo
					onClose={() => setSelectedGroup(null)}
					onAddVariants={handleAddVariants}
					getLocationQty={() => 0}
					getAvailableQty={() => 9999}
					getReservedQty={() => 0}
				/>
			)}
		</div>
	)
}
